import COS from "cos-js-sdk-v5";
import { v4 as uuidv4 } from "uuid";
import type {
  CreateBuildCosOptions,
  CosStsScope,
  PutObjectToOssParams,
  STSResponse,
  UploadFileToOssParams,
} from "./types";
import type { UploadFileObj } from "./cos-helper";

export type { CreateBuildCosOptions, CosStsScope, PutObjectToOssParams, STSResponse, UploadFileToOssParams };

/**
 * 创建 buildCos 工厂：STS URL 与 Authorization 由各应用注入，便于 Web / WebView 等不同鉴权方式。
 */
export function createBuildCos(options: CreateBuildCosOptions): (fileKey?: string) => COS {
  const getReferer =
    options.getReferer ??
    (() => (typeof window !== "undefined" ? window.location.origin : ""));
  const getRequestId = options.getRequestId ?? (() => uuidv4());

  /** STS 失败时也必须结束授权回调，否则 COS SDK 会一直挂起 */
  const failAuthorization = (
    callback: (params: {
      TmpSecretId: string;
      TmpSecretKey: string;
      SecurityToken?: string;
      StartTime: number;
      ExpiredTime: number;
      ScopeLimit?: boolean;
    }) => void,
  ) => {
    callback({
      TmpSecretId: "",
      TmpSecretKey: "",
      SecurityToken: "",
      StartTime: 0,
      ExpiredTime: 0,
    });
  };

  return function buildCos(fileKey?: string) {
    return new COS({
      getAuthorization(cosOptions, callback) {
        const scope: CosStsScope = {
          action: "name/cos:PutObject",
          region: cosOptions.Region,
          bucket: cosOptions.Bucket,
          prefix: fileKey,
        };

        const headers: Record<string, string> = {
          Accept: "application/json",
          Authorization: options.getAuthorizationHeader() ?? "",
          Referer: getReferer(),
          RequestId: getRequestId(),
        };

        fetch(options.buildStsUrl(scope), {
          method: "GET",
          headers,
        })
          .then((rsp) => rsp.json())
          .then((body: { data?: STSResponse }) => {
            const data = body.data;
            if (!data?.Credentials) {
              console.error("STS response missing credentials", body);
              failAuthorization(callback);
              return;
            }
            callback({
              TmpSecretId: data.Credentials.TmpSecretId,
              TmpSecretKey: data.Credentials.TmpSecretKey,
              SecurityToken: data.Credentials.Token,
              StartTime: data.StartTime,
              ExpiredTime: data.ExpiredTime,
              ScopeLimit: true,
            });
          })
          .catch((err) => {
            console.error(err);
            failAuthorization(callback);
          });
      },
    });
  };
}

export function uploadFileToOss({
  cos,
  file,
  fileKey,
  bucket,
  region,
  onProgress,
}: UploadFileToOssParams): Promise<COS.UploadFileResult> {
  return cos.uploadFile({
    Bucket: bucket,
    Region: region,
    Key: fileKey,
    Body: file,
    onProgress: onProgress,
  });
}

export function putObjectToOss({
  cos,
  key,
  file,
  onProgress,
  bucket,
  region,
}: PutObjectToOssParams): Promise<COS.UploadFileResult> {
  return cos.putObject({
    Bucket: bucket,
    Region: region,
    Key: key,
    Body: file,
    onProgress: onProgress,
  });
}

export function uploadFilesToOss(
  cos: COS,
  files: UploadFileObj[],
  bucket: string,
  region: string,
  onProgress: COS.onProgress,
  onFileFinish?: COS.onFileFinish,
): Promise<COS.UploadFilesResult> {
  const fp: Array<{
    Bucket: string;
    Region: string;
    Key: string;
    Body: File;
    onTaskReady?: (taskId: unknown) => void;
  }> = [];
  files.forEach((file) => {
    fp.push({
      Bucket: bucket,
      Region: region,
      Key: file.fileName,
      Body: file.fileObj,
      onTaskReady: file.onTaskReady,
    });
  });

  return cos.uploadFiles({
    files: fp,
    SliceSize: 1024 * 1024 * 10,
    onProgress: onProgress,
    onFileFinish: onFileFinish,
  });
}
